import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  // Set up axios interceptor for token
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchCurrentUser]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  // Method to update user name locally (after saving settings)
  const updateUserName = (newName) => {
    if (user) {
      setUser({ ...user, name: newName });
    }
  };

  // Method to refresh user data from server
  const refreshUser = async () => {
    if (token) {
      await fetchCurrentUser();
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUserName,
    refreshUser,
    isAuthenticated: !!user,
    isAdvisor: user?.role === 'advisor',
    isManager: user?.role === 'manager',
    isSuperAdmin: user?.role === 'super_admin',
    isClient: user?.role === 'client',
    isPendingApproval: user?.status === 'pending_approval',
    isFreePlan: user?.plan === 'plus',
    isProPlan: user?.plan === 'pro_plus' || !user?.plan,
    userPlan: user?.plan || 'pro_plus'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};